import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import DOMPurify from "dompurify";
import { Lock, CreditCard, ShieldAlert } from "lucide-react";
import DynamicForm from "@/pages/DynamicForm";

const API = "/api";

export default function CustomPage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const { i18n } = useTranslation();
  const fr = (i18n.language || "en").startsWith("fr");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null); // {code}

  useEffect(() => {
    setLoading(true); setPage(null); setDenied(null);
    axios.get(`${API}/pages/${slug}`, { withCredentials: true })
      .then((res) => setPage(res.data))
      .catch((err) => {
        const code = err.response?.data?.detail?.code;
        if (err.response?.status === 401 || err.response?.status === 403) setDenied({ code: code || "ACCESS_DENIED" });
        else setDenied({ code: "NOT_FOUND" });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#050814] flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#FFC928] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (denied) {
    const map = {
      LOGIN_REQUIRED: { icon: Lock, title: fr ? "Connexion requise" : "Login Required", msg: fr ? "Connectez-vous pour voir cette page." : "Please sign in to view this page.", cta: { to: "/account", label: fr ? "Se connecter" : "Sign In" } },
      PREMIUM_REQUIRED: { icon: CreditCard, title: fr ? "Accès Premium requis" : "Premium Required", msg: fr ? "Cette page est réservée aux membres Premium." : "This page is for Premium members.", cta: { to: "/membership", label: fr ? "Voir les offres" : "View Plans" } },
      GUESTS_ONLY: { icon: ShieldAlert, title: fr ? "Réservé aux visiteurs" : "Guests Only", msg: fr ? "Cette page n'est pas disponible pour les comptes connectés." : "This page is not available to signed-in accounts." },
      ACCESS_DENIED: { icon: ShieldAlert, title: fr ? "Accès refusé" : "Access Denied", msg: fr ? "Vous n'avez pas accès à cette page." : "You do not have access to this page." },
      NOT_FOUND: { icon: ShieldAlert, title: fr ? "Page introuvable" : "Page Not Available", msg: fr ? "Cette page n'existe pas ou n'est plus disponible." : "This page doesn't exist or is no longer available." },
    };
    const d = map[denied.code] || map.ACCESS_DENIED;
    const Icon = d.icon;
    return (
      <div className="min-h-screen bg-[#050814] flex items-center justify-center px-6" data-testid={`page-access-${denied.code}`}>
        <div className="text-center max-w-sm">
          <Icon size={44} className="text-[#FFC928] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">{d.title}</h1>
          <p className="text-[#94A3B8] text-sm mb-5">{d.msg}</p>
          {d.cta && <Link to={d.cta.to} className="inline-block px-5 py-2.5 rounded-xl bg-[#FFC928] text-black font-bold text-sm">{d.cta.label}</Link>}
        </div>
      </div>
    );
  }

  if (!page) return null;

  const title = fr ? page.title_fr : page.title_en;
  const content = fr ? page.content_fr : page.content_en;
  const customHtml = fr ? page.custom_html_fr : page.custom_html_en;

  return (
    <div className="min-h-screen bg-[#050814] text-white" data-testid="custom-page">
      {page.hero_image && <img src={page.hero_image} alt={title} className="w-full h-56 sm:h-72 object-cover" />}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "Poppins" }} data-testid="custom-page-title">{title}</h1>
        {content && (
          <div className="prose prose-invert max-w-none" data-testid="custom-page-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
        )}
        {customHtml && (
          <iframe title="page-custom-html" sandbox="allow-scripts allow-same-origin allow-popups"
            className="w-full mt-8 rounded-xl border border-white/[0.06]" style={{ minHeight: 200 }}
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;font-family:system-ui,sans-serif;color:#e2e8f0;background:transparent}</style></head><body>${customHtml}</body></html>`}
            data-testid="custom-page-html" />
        )}
        {page.embedded_form_key && (
          <div className="mt-10" data-testid="custom-page-form">
            <DynamicForm formKey={page.embedded_form_key} />
          </div>
        )}
      </div>
    </div>
  );
}
